import React, {
  useEffect,
  useMemo,
  Fragment,
  useCallback,
} from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import type { StripeElementsOptions } from '@stripe/stripe-js';

import {
  AuthFormLayout,
  AuthLayout,
  Button,
  ButtonStyleEnum,
  Separator,
  PlanPriceView,
  Line,
  CouponForm,
} from '@components';
import {
  AuthNavigatorNavigationPropType,
  AuthNavigatorRoutePropType,
  screens,
} from '@navigation';
import { formatCurrencyToGBP, IS_WEB, passIf } from '@utils';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useAuthModel } from '@models/AuthModel';
import { ToastService } from '@services';
import {
  useCouponCodeStore,
  CouponType,
  useNavOptionsSet,
} from '@hooks';
import { StripeElementsProvider } from '@context';
import { AddPaymentMethodForm } from './components/AddPaymentMethodForm';
import { PAYMENT_METHOD_TYPES } from '@constants';

type NavigationPropType =
  AuthNavigatorNavigationPropType<'AddPaymentMethod'>;
type RoutePropType = AuthNavigatorRoutePropType<'AddPaymentMethod'>;

export const AddPaymentMethodScreen = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const auth = useAuthModel();
  const couponStore = useCouponCodeStore();
  const coupon = couponStore.getCouponForTariffPlan(
    route.params.tariffPlan,
  );

  useNavOptionsSet({ title: t('addPaymentMethod.title') });

  useEffect(() => {
    // On web user can reach this page by entering route in address bar
    // We check if previous step was made (filled credentials and info form)
    // if not then navigate a user back to sign up screen
    if (IS_WEB && !auth.signUpDraft) {
      navigateToSignUp();
    }
  }, []);

  function navigateToSignUp() {
    nav.navigate(screens.SignUp);
  }

  function successSignUp() {
    auth.clearSignUpDraft();

    ToastService.showSuccess(t('addPaymentMethod.successMessage'));

    navigateToSignIn();
  }

  function navigateToSignIn() {
    const name = screens.SignIn;
    const params = {
      invite: route.params.invite,
      inviteVideoId: route.params.inviteVideoId,
    };

    // We use reset method here instead of navigate
    // to properly wipe previous screens and improve user experience
    nav.reset({ index: 0, routes: [{ name, params }] });
  }

  const tKeyForPlan = `common.subscriptionPlans.${route.params.tariffPlan}`;
  const plan = viewer.tariffPlans.byCode[route.params.tariffPlan];

  // Actual price with discounts (promo or coupon)
  const formattedCurrentPrice = useMemo(() => {
    if (coupon) {
      return formatCurrencyToGBP(coupon.newAmount);
    } else {
      return plan?.price.formattedCurrent ?? '';
    }
  }, [coupon, plan?.price.formattedCurrent]);

  const currentPrice = useMemo(() => {
    if (coupon) {
      return Number(coupon.newAmount);
    } else {
      return Number(plan?.price.current ?? 0);
    }
  }, [coupon, plan?.price.formattedCurrent]);

  // Regular price without discounts or price with promo discount or for
  // case when use applied coupon
  const formattedRegularPrice = useMemo(() => {
    if (coupon) {
      if (plan?.price.hasDiscount) {
        return plan?.price.formattedCurrent;
      } else {
        return plan?.price.formattedRegular;
      }
    } else if (plan?.price.hasDiscount) {
      return plan?.price.formattedRegular;
    }

    return '';
  }, [
    coupon,
    plan?.price.hasDiscount,
    plan?.price.formattedRegular,
    plan?.price.formattedCurrent,
  ]);

  const formattedAutoupgradePrice =
    plan?.autoupgradePlan?.price.formattedRegular ?? '';

  const subtitle = useMemo(() => {
    if (coupon) {
      return t('addPaymentMethod.couponDiscount', {
        percentage: coupon.discount,
      });
    }

    if (plan?.price.isFree) {
      return t(`${tKeyForPlan}.subtitleFree`);
    }

    return t(`${tKeyForPlan}.subtitle`, {
      price: formattedAutoupgradePrice,
      defaultValue: null,
    });
  }, [tKeyForPlan, formattedAutoupgradePrice, coupon]);

  const applyCoupon = useCallback((couponData: CouponType) => {
    const couponItem = couponData.find(
      (i) => i.tariffPlan === route.params.tariffPlan,
    );

    if (couponItem) {
      couponStore.applyCoupon(couponData);
    } else {
      ToastService.showError(t('errors.couponNotApplicable'));
    }
  }, []);

  const elementOptions = useMemo<StripeElementsOptions>(() => {
    return {
      mode: 'subscription',
      amount: Math.round(currentPrice * 100),
      currency: plan?.price.currency.toLowerCase() ?? 'gbp',
      paymentMethodTypes: PAYMENT_METHOD_TYPES,
    };
  }, [currentPrice, plan?.price.currency]);

  return (
    <AuthLayout
      onPressHeaderButton={navigateToSignIn}
      headerButtonText={t('addPaymentMethod.signIn')}
      headerButtonSideText={t('addPaymentMethod.haveAccount')}
    >
      <AuthFormLayout
        title={t('addPaymentMethod.title')}
        topText={passIf(
          IS_WEB,
          t('addPaymentMethod.stepOf', { step: 3, ofSteps: 3 }),
        )}
        bottomText={passIf(
          !IS_WEB,
          t('addPaymentMethod.stepOf', { step: 3, ofSteps: 3 }),
        )}
      >
        <PlanPriceView
          currentPrice={formattedCurrentPrice}
          regularPrice={formattedRegularPrice}
          title={t(`${tKeyForPlan}.title`)}
          subtitle={subtitle}
        />

        <Separator size="$7" />

        {IS_WEB ? (
          <Fragment>
            {/* Users that are using invitation cannot use coupon */}
            {!route.params.invite && (
              <Fragment>
                <CouponForm
                  onApply={applyCoupon}
                  isApplied={couponStore.isAppliedForTariffPlan(
                    route.params.tariffPlan,
                  )}
                />
                <Separator size="$7" />
                <Line />
              </Fragment>
            )}

            <Separator size="$7" />
            <StripeElementsProvider options={elementOptions}>
              <AddPaymentMethodForm
                onSuccessSignUp={successSignUp}
                couponStore={couponStore}
              />
            </StripeElementsProvider>
          </Fragment>
        ) : (
          <AddPaymentMethodForm
            onSuccessSignUp={successSignUp}
            couponStore={couponStore}
          />
        )}

        <Separator size="$5" />

        <Button
          buttonStyle={ButtonStyleEnum.Outline}
          iconLeft="small-arrow-left-light"
          onPress={nav.goBack}
        >
          {t('addPaymentMethod.goBack')}
        </Button>

        <Separator size="$6" />
      </AuthFormLayout>
    </AuthLayout>
  );
});
